import React, { useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { uploadActivity } from "../../services/workouts.service";

type UploadState = "idle" | "processing" | "done" | "error";

interface UploadCardProps {
  onUploaded: () => void;
}

export const UploadCard: React.FC<UploadCardProps> = ({ onUploaded }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [fileName, setFileName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const startUpload = async (file: File) => {
    setFileName(file.name);
    setUploadState("processing");
    setDragOver(false);
    try {
      const token = await getAccessTokenSilently();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const activityName = file.name.replace(/\.fit$/i, "");
      const { error } = await uploadActivity(token, file, activityName, timezone);
      if (error) {
        setErrorMessage(error.message);
        setUploadState("error");
        return;
      }
      setUploadState("done");
      onUploaded();
    } catch (e: any) {
      setErrorMessage(e.message);
      setUploadState("error");
    }
  };

  const reset = () => {
    setUploadState("idle");
    setFileName("");
    setErrorMessage("");
  };

  return (
    <section className="home__card home__upload">
      <div className="home__card-title">Upload activity</div>
      <input
        type="file"
        accept=".fit"
        ref={fileInputRef}
        onChange={(e) => {
          const file = e.target.files && e.target.files[0];
          if (file) {
            startUpload(file);
          }
          e.target.value = "";
        }}
        style={{ display: "none" }}
      />
      {uploadState === "idle" && (
        <div
          className={
            "home__dropzone" + (dragOver ? " home__dropzone--drag" : "")
          }
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files && e.dataTransfer.files[0];
            if (file) {
              startUpload(file);
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!dragOver) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
        >
          <div className="home__dropzone-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#f0862f"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12"></path>
            </svg>
          </div>
          <div className="home__dropzone-title">Drop a .fit file here</div>
          <div className="home__dropzone-sub">or browse files</div>
          <div className="home__dropzone-note">Garmin · Wahoo · COROS</div>
        </div>
      )}
      {uploadState === "processing" && (
        <div className="home__dropzone home__dropzone--processing">
          <div className="home__pulse"></div>
          <div className="home__dropzone-title">Processing {fileName}…</div>
          <div className="home__dropzone-muted">
            Extracting laps, HR and pace data
          </div>
        </div>
      )}
      {uploadState === "done" && (
        <div className="home__dropzone home__dropzone--done">
          <div className="home__dropzone-icon home__dropzone-icon--done">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#3ec9a7"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5"></path>
            </svg>
          </div>
          <div className="home__dropzone-title">{fileName} processed</div>
          <div className="home__dropzone-muted">
            Added to your workouts and analysis.
          </div>
          <div className="home__dropzone-action" onClick={reset}>
            Upload another
          </div>
        </div>
      )}
      {uploadState === "error" && (
        <div className="home__dropzone">
          <div className="home__dropzone-title">Upload failed</div>
          <div className="home__dropzone-error">{errorMessage}</div>
          <div className="home__dropzone-action" onClick={reset}>
            Try again
          </div>
        </div>
      )}
    </section>
  );
};
